import { DownloadSimpleIcon } from '@phosphor-icons/react';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Pagination,
  SearchField,
  Spinner,
} from '@skupreme/shared/ui';
import { brand } from '@utility/config';
import { DateFormat, DateUtility } from '@utility/date';
import { useInvoiceFiles, useInvoiceLogs } from '../../hooks/use-invoice';
import type { InvoiceLog, ProviderInvoiceDetail } from '../../lib/invoices';

function LogEntry({ log }: { log: InvoiceLog }) {
  /* No one named: the system did it, under the brand's mark. */
  const by = log.performedBy?.name;

  return (
    <li className="sk:flex sk:flex-col sk:gap-1 sk:border-b sk:border-rule sk:py-2.5 sk:last:border-b-0">
      <span className="sk:text-dense sk:tracking-dense sk:text-fg">
        {log.message}
      </span>
      <span className="sk:flex sk:items-center sk:justify-between sk:gap-2 sk:text-xs sk:text-fg-tertiary">
        <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5">
          {by ? (
            <Avatar name={by} size="xs" />
          ) : (
            <Avatar src={brand.icon} name={brand.name} size="xs" />
          )}
          <span className="sk:truncate">{by ?? brand.name}</span>
        </span>
        <span className="sk:shrink-0 sk:tabular-nums">
          {DateUtility.formatDate(log.createdAt, DateFormat.barChartMonth)}
        </span>
      </span>
    </li>
  );
}

export type InvoiceHistoryProps = {
  providerId: string;
  invoice: ProviderInvoiceDetail | null;
};

/** What happened to the invoice, newest first, with a CSV of it all. */
export function InvoiceHistory({ providerId, invoice }: InvoiceHistoryProps) {
  const invoiceId = invoice?._id ?? '';
  const { data, totalData, loading, filter, filterChanged, pageChanged } =
    useInvoiceLogs(providerId, invoiceId);
  const { exportLogs } = useInvoiceFiles(providerId, invoice);

  return (
    <Card
      title="History"
      action={
        <Button
          size="sm"
          isDisabled={!invoice}
          isPending={exportLogs.isPending}
          onPress={() => exportLogs.mutate()}
        >
          <DownloadSimpleIcon size={14} weight="bold" />
          Export
        </Button>
      }
      bodyClassName="sk:flex sk:flex-col sk:gap-3"
    >
      <SearchField
        aria-label="Search history"
        placeholder="Category, service or message"
        value={String(filter.search ?? '')}
        onChange={value => filterChanged('search', value)}
        isLoading={loading && !!filter.search}
        className="sk:w-full"
      />
      {loading && !data.length ? (
        <Spinner size="sm" label="Loading history" />
      ) : data.length ? (
        <ul className="sk:m-0 sk:list-none sk:p-0">
          {data.map(log => (
            <LogEntry key={log._id} log={log} />
          ))}
        </ul>
      ) : (
        <EmptyState title="No history yet" />
      )}
      {totalData > filter.limit ? (
        <Pagination
          page={filter.page}
          pageSize={filter.limit}
          total={totalData}
          onPageChange={pageChanged}
          onPageSizeChange={size => filterChanged('limit', size)}
        />
      ) : null}
    </Card>
  );
}
